import { ContextType, ContextTabType, ContextObject, CONTEXT_TYPE_MAP, CONTEXT_ICONS, CONTEXT_COLORS } from './ContextManager';
import moment from 'moment';

/**
 * Context utility functions for managing context types, IDs, and related operations
 */

// Context validation utilities
export const validateContextType = (contextType: string): contextType is ContextType => {
  return Object.values(CONTEXT_TYPE_MAP).includes(contextType as ContextType);
};

export const validateContextId = (contextId: string | number): boolean => {
  return !!(contextId && (typeof contextId === 'string' ? contextId.trim() !== '' : !isNaN(contextId)));
};

export const validateContext = (context: Partial<ContextObject>): boolean => {
  return validateContextType(context.contextType || '') && validateContextId(context.contextId || '');
};

// Context type conversion utilities
export const tabTypeToContextType = (tabType: ContextTabType): ContextType => {
  return CONTEXT_TYPE_MAP[tabType];
};

export const contextTypeToTabType = (contextType: ContextType): ContextTabType | null => {
  const entry = Object.entries(CONTEXT_TYPE_MAP).find(([_, value]) => value === contextType);
  return entry ? (entry[0] as ContextTabType) : null;
};

// Context display utilities
export const getContextDisplayName = (context: ContextObject): string => {
  return context.title || `${context.contextType} #${context.contextId}`;
};

export const getContextIcon = (contextType: ContextType): string => {
  return CONTEXT_ICONS[contextType] || 'assignment';
};

export const getContextColor = (contextType: ContextType): string => {
  return CONTEXT_COLORS[contextType] || '#757575';
};

// Context creation utilities
export const createContext = (
  contextType: ContextType, 
  contextId: string | number, 
  metadata?: Partial<ContextObject>
): ContextObject => {
  return {
    contextType,
    contextId,
    ...metadata,
  };
};

export const createContextFromAPI = (
  contextType: string, 
  contextId: string | number, 
  metadata?: Partial<ContextObject>
): ContextObject | null => {
  if (!validateContextType(contextType) || !validateContextId(contextId)) {
    return null;
  }
  
  return createContext(contextType as ContextType, contextId, metadata);
};

// Context filtering utilities
export const filterContextsByType = (contexts: ContextObject[], contextType: ContextType): ContextObject[] => {
  return contexts.filter(context => context.contextType === contextType);
};

export const filterContextsById = (contexts: ContextObject[], contextId: string | number): ContextObject[] => {
  return contexts.filter(context => context.contextId === contextId);
};

export const findContextById = (contexts: ContextObject[], contextId: string | number, contextType?: ContextType): ContextObject | null => {
  const filtered = contextType 
    ? filterContextsByType(contexts, contextType)
    : contexts;
  
  return filtered.find(context => context.contextId === contextId) || null;
};

// Context comparison utilities
export const isSameContext = (context1: ContextObject, context2: ContextObject): boolean => {
  return context1.contextType === context2.contextType && context1.contextId === context2.contextId;
};

export const isContextInList = (context: ContextObject, contexts: ContextObject[]): boolean => {
  return contexts.some(c => isSameContext(c, context));
};

// Context sorting utilities
export const sortContextsByType = (contexts: ContextObject[]): ContextObject[] => {
  return [...contexts].sort((a, b) => a.contextType.localeCompare(b.contextType));
};

export const sortContextsById = (contexts: ContextObject[]): ContextObject[] => {
  return [...contexts].sort((a, b) => {
    const aId = typeof a.contextId === 'string' ? parseInt(a.contextId) : a.contextId;
    const bId = typeof b.contextId === 'string' ? parseInt(b.contextId) : b.contextId;
    return aId - bId;
  });
};

export const sortContextsByDate = (contexts: ContextObject[], field: 'createdAt' | 'updatedAt' = 'createdAt'): ContextObject[] => {
  return [...contexts].sort((a, b) => {
    const aDate = moment(a[field] || 0).valueOf();
    const bDate = moment(b[field] || 0).valueOf();
    return bDate - aDate; // Most recent first
  });
};

// Context API utilities
export const formatContextForAPI = (context: ContextObject): { p_context_type: string; p_context_id: string | number } => {
  return {
    p_context_type: context.contextType,
    p_context_id: context.contextId,
  };
};

export const formatContextsForAPI = (contexts: ContextObject[]): Array<{ p_context_type: string; p_context_id: string | number }> => {
  return contexts.map(formatContextForAPI);
};

export const parseContextFromAPI = (contextType: string, contextId: string | number): ContextObject | null => {
  return createContextFromAPI(contextType, contextId);
};

// Context search utilities
export const searchContexts = (contexts: ContextObject[], query: string): ContextObject[] => {
  const lowerQuery = query.toLowerCase();
  return contexts.filter(context => 
    context.contextType.toLowerCase().includes(lowerQuery) ||
    context.title?.toLowerCase().includes(lowerQuery) ||
    context.description?.toLowerCase().includes(lowerQuery) ||
    String(context.contextId).includes(lowerQuery)
  );
};

// Context grouping utilities
export const groupContextsByType = (contexts: ContextObject[]): Record<ContextType, ContextObject[]> => {
  return contexts.reduce((groups, context) => {
    if (!groups[context.contextType]) {
      groups[context.contextType] = [];
    }
    groups[context.contextType].push(context);
    return groups;
  }, {} as Record<ContextType, ContextObject[]>);
};

export const groupContextsByFacility = (contexts: ContextObject[]): Record<string, ContextObject[]> => {
  return contexts.reduce((groups, context) => {
    const facilityKey = String(context.facilityId || 'unknown');
    if (!groups[facilityKey]) {
      groups[facilityKey] = [];
    }
    groups[facilityKey].push(context);
    return groups;
  }, {} as Record<string, ContextObject[]>);
};

// Context statistics utilities
export const getContextStats = (contexts: ContextObject[]) => {
  const byType = groupContextsByType(contexts);
  const byFacility = groupContextsByFacility(contexts);
  
  return {
    total: contexts.length,
    byType: Object.entries(byType).reduce((acc, [type, contexts]) => {
      acc[type] = contexts.length;
      return acc;
    }, {} as Record<ContextType, number>),
    byFacility: Object.entries(byFacility).reduce((acc, [facility, contexts]) => {
      acc[facility] = contexts.length;
      return acc;
    }, {} as Record<string, number>),
    mostRecent: contexts.length > 0 ? sortContextsByDate(contexts)[0] : null,
  };
};

// Context validation for forms
export const validateContextForm = (formData: Partial<ContextObject>): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];
  
  if (!formData.contextType) {
    errors.push('Context type is required');
  } else if (!validateContextType(formData.contextType)) {
    errors.push('Invalid context type');
  }
  
  if (!formData.contextId) {
    errors.push('Context ID is required');
  } else if (!validateContextId(formData.contextId)) {
    errors.push('Invalid context ID');
  }
  
  return {
    isValid: errors.length === 0,
    errors,
  };
};

// Context constants
export const CONTEXT_CONSTANTS = {
  MAX_HISTORY_SIZE: 10,
  MAX_CONTEXTS_PER_TYPE: 100,
  DEFAULT_CONTEXT_TYPES: Object.values(CONTEXT_TYPE_MAP),
  DEFAULT_TAB_TYPES: Object.keys(CONTEXT_TYPE_MAP) as ContextTabType[],
} as const;

export default {
  validateContextType,
  validateContextId,
  validateContext,
  tabTypeToContextType,
  contextTypeToTabType,
  getContextDisplayName,
  getContextIcon,
  getContextColor,
  createContext,
  createContextFromAPI,
  filterContextsByType,
  filterContextsById,
  findContextById,
  isSameContext,
  isContextInList,
  sortContextsByType,
  sortContextsById,
  sortContextsByDate,
  formatContextForAPI,
  formatContextsForAPI,
  parseContextFromAPI,
  searchContexts,
  groupContextsByType,
  groupContextsByFacility,
  getContextStats,
  validateContextForm,
  CONTEXT_CONSTANTS,
};
